import { t } from '@lingui/macro'
import { useCallback, useEffect, useState } from 'react'
import type { AuditChangeLogResponseDTO } from '../client/models/AuditChangeLogResponseDTO'
import type { AuditChangeType } from '../client/models/AuditChangeType'
import type { AuditOperation } from '../client/models/AuditOperation'
import type { PaginateMeta } from '../client/models/PaginateMeta'
import { AspenAuditLogsService } from '../client/services/AspenAuditLogsService'

export type SubjectType = 'candidate' | 'patient' | 'care-partner'

export interface UseSubjectAuditHistoryProps {
    subjectType: SubjectType
    subjectId: string
    userId?: string
    startDate?: string
    endDate?: string
    operation?: AuditOperation
    changeType?: AuditChangeType
    sort?: string
    page?: number
    limit?: number
    enabled?: boolean
}

export interface UseSubjectAuditHistoryReturn {
    auditLogs: AuditChangeLogResponseDTO[]
    meta: PaginateMeta | null
    isLoading: boolean
    error: string | null
    refetch: () => Promise<void>
    setPage: (page: number) => void
    setLimit: (limit: number) => void
    setSort: (sort: string) => void
}

export const useSubjectAuditHistory = ({
    subjectType,
    subjectId,
    userId,
    startDate,
    endDate,
    operation,
    changeType,
    sort,
    page = 1,
    limit = 20,
    enabled = true
}: UseSubjectAuditHistoryProps): UseSubjectAuditHistoryReturn => {
    const [auditLogs, setAuditLogs] = useState<AuditChangeLogResponseDTO[]>([])
    const [meta, setMeta] = useState<PaginateMeta | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [currentPage, setCurrentPage] = useState(page)
    const [currentLimit, setCurrentLimit] = useState(limit)
    const [currentSort, setCurrentSort] = useState(sort || 'created_dttm:desc')

    const fetchAuditHistory = useCallback(async () => {
        if (!enabled || !subjectType || !subjectId) {
            return
        }

        setIsLoading(true)
        setError(null)
        try {
            const response = await AspenAuditLogsService.getSubjectHistoryAuditLogsSubjectSubjectTypeSubjectIdGet({
                subjectType,
                subjectId,
                userId,
                startDate,
                endDate,
                operation,
                changeType,
                sort: currentSort,
                page: currentPage,
                limit: currentLimit
            })
            setAuditLogs(response.data)
            setMeta(response.meta)
        } catch (err) {
            setError(t`Failed to fetch audit history. Please try again.`)
            console.error(t`Failed to fetch audit history:`, err)
        } finally {
            setIsLoading(false)
        }
    }, [enabled, subjectType, subjectId, userId, startDate, endDate, operation, changeType, currentSort, currentPage, currentLimit])

    useEffect(() => {
        fetchAuditHistory()
    }, [fetchAuditHistory])

    const refetch = useCallback(async () => {
        await fetchAuditHistory()
    }, [fetchAuditHistory])

    return {
        auditLogs,
        meta,
        isLoading,
        error,
        refetch,
        setPage: setCurrentPage,
        setLimit: setCurrentLimit,
        setSort: setCurrentSort
    }
}
